import type { FieldADTValue, LayoutSize, SizeConfigure, VariableADTvalue } from '@lighthouse/core'
import { useMemo } from 'react'

import { useFlowLayoutContext } from '../../components/LayoutEngine/Context'

function resolveSizeConfigure(
    configure: SizeConfigure | undefined,
    parse: (v?: VariableADTvalue) => FieldADTValue | undefined
): SizeConfigure | undefined {
    if (!configure?.variable) return configure
    const resolved = parse(configure.variable)
    if (!resolved) return configure
    const numValue = Number((resolved as { value?: unknown }).value)
    if (Number.isFinite(numValue)) {
        return { ...configure, size: numValue }
    }
    return configure
}

/**
 * 将 LayoutSize 中的 variable 字段解析为静态数值。
 * 当 SizeConfigure.variable 存在时，通过 LayoutEngine context 注入的解析器求值，
 * 结果写入 size 字段供 getSizeStyle / getWidthSize / getHeightSize 使用。
 */
export function useResolveSizeLayout(layoutSize: LayoutSize | undefined): LayoutSize | undefined {
    const { useParseVariableWithSubscription } = useFlowLayoutContext()

    const parseWidth = useParseVariableWithSubscription(layoutSize?.width?.variable)
    const parseHeight = useParseVariableWithSubscription(layoutSize?.height?.variable)
    const parseMinWidth = useParseVariableWithSubscription(layoutSize?.minWidth?.variable)
    const parseMaxWidth = useParseVariableWithSubscription(layoutSize?.maxWidth?.variable)
    const parseMinHeight = useParseVariableWithSubscription(layoutSize?.minHeight?.variable)
    const parseMaxHeight = useParseVariableWithSubscription(layoutSize?.maxHeight?.variable)

    return useMemo(() => {
        if (!layoutSize) return undefined

        const hasAnyVariable =
            layoutSize.width?.variable ||
            layoutSize.height?.variable ||
            layoutSize.minWidth?.variable ||
            layoutSize.maxWidth?.variable ||
            layoutSize.minHeight?.variable ||
            layoutSize.maxHeight?.variable

        if (!hasAnyVariable) return layoutSize

        return {
            ...layoutSize,
            width: resolveSizeConfigure(layoutSize.width, parseWidth) ?? layoutSize.width,
            height: resolveSizeConfigure(layoutSize.height, parseHeight) ?? layoutSize.height,
            minWidth: layoutSize.minWidth
                ? resolveSizeConfigure(layoutSize.minWidth as SizeConfigure, parseMinWidth)
                : undefined,
            maxWidth: layoutSize.maxWidth
                ? resolveSizeConfigure(layoutSize.maxWidth as SizeConfigure, parseMaxWidth)
                : undefined,
            minHeight: layoutSize.minHeight
                ? resolveSizeConfigure(layoutSize.minHeight as SizeConfigure, parseMinHeight)
                : undefined,
            maxHeight: layoutSize.maxHeight
                ? resolveSizeConfigure(layoutSize.maxHeight as SizeConfigure, parseMaxHeight)
                : undefined
        }
    }, [layoutSize, parseWidth, parseHeight, parseMinWidth, parseMaxWidth, parseMinHeight, parseMaxHeight])
}
